import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Divider,
  Flex,
  Modal,
  Progress,
  Spin,
  Steps,
  theme,
  type ModalProps
} from 'antd'
import { ChevronDown, ChevronUp, Info, RotateCcw } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  integrationSetupJobsListQueryKey,
  integrationSetupJobsRetryCreateMutation,
  SetupJobStatusEnum,
  type Organization,
  type OrganizationCreate,
  type SetupJob
} from 'src/api/main'
import { Text } from 'src/components'
import { useShowApiErrors } from 'src/utils'

import { useSetupProgress } from './hooks'
import { useStyles } from './styles'

const MAX_ITEMS = 5

type Props = {
  organization?: OrganizationCreate | Organization
  parentId?: number
} & ModalProps

type StepItem = {
  title: string
  status: 'finish' | 'process' | 'wait' | 'error'
  description: ReactNode
  icon?: ReactNode
}

export const SetupProgressStepperModal = ({
  parentId,
  organization,
  ...props
}: Props) => {
  /** `null` = follow auto rule (collapse when list is long); otherwise user override */
  const [collapseOverride, setCollapseOverride] = useState<boolean | null>(null)
  const showApiErrors = useShowApiErrors()

  const { t } = useTranslation()
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()

  const {
    jobs,
    total: totalCount,
    percent,
    hasError,
    allDone,
    isFetching
  } = useSetupProgress(parentId, Boolean(props.open))

  const progressStatus: 'exception' | 'success' | 'active' = hasError
    ? 'exception'
    : allDone
      ? 'success'
      : 'active'

  const { mutateAsync, isPending } = useMutation({
    ...integrationSetupJobsRetryCreateMutation(),
    onSuccess: () =>
      // because our custom hook useInvalidatePredicateQueries is refetching ALL queries with id, no matter what the query id is, we need to refetch only the specific query using default refetchQueries method
      queryClient.refetchQueries({
        queryKey: integrationSetupJobsListQueryKey({
          query: { parent: Number(parentId) }
        })
      }),
    onError: (e) => showApiErrors(e)
  })

  const results = jobs
  const tooManyItems = results.length > MAX_ITEMS
  const isCollapsed =
    collapseOverride !== null ? collapseOverride : tooManyItems

  const items = useMemo<StepItem[]>(() => {
    type JobWithExtra = SetupJob & {
      additional_data?: Record<string, unknown>
    }
    const newItems = results.map((job) => {
      let stepStatus: 'finish' | 'process' | 'wait' | 'error' = 'wait'
      let icon: ReactNode

      if (job.status === SetupJobStatusEnum.FINISHED) {
        stepStatus = 'finish'
      } else if (job.status === SetupJobStatusEnum.IN_PROCESS) {
        stepStatus = 'process'
        icon = <Spin size="small" />
      } else if (job.status === SetupJobStatusEnum.FAILED) {
        stepStatus = 'error'
      }

      const extra = (job as JobWithExtra).additional_data

      return {
        order: job.pipeline_step.order,
        title: job.pipeline_step.template_name,
        status: stepStatus,
        icon,
        description: extra?.error ? (
          <Text color="colorError">{(extra.error as string) ?? ''}</Text>
        ) : stepStatus === 'process' ? (
          <Text color="colorTextSecondary">{t('in_progress')}</Text>
        ) : null
      }
    })

    const sorted = newItems.sort((a, b) => a.order - b.order)

    return isCollapsed ? sorted.slice(0, MAX_ITEMS) : sorted
  }, [results, t, isCollapsed])

  const onRetry = async () => {
    if (isPending || !parentId) {
      return
    }

    await mutateAsync({ path: { id: Number(parentId) } })
  }

  const onOk = () => {
    if (!organization?.domain_name) return
    const url = `https://${organization.domain_name}.${import.meta.env.VITE_APP_DOMAIN_URL}`
    window.open(url, '_blank')
  }

  const onToggleCollapse = () => {
    setCollapseOverride((prev) => {
      const current = prev !== null ? prev : tooManyItems

      return !current
    })
  }

  const showSpinner = isFetching && hasError

  return (
    <Modal
      title={allDone ? t('setup_completed') : `${t('loading_data')}...`}
      width={650}
      centered
      destroyOnHidden
      mask={{ closable: false }}
      okButtonProps={{
        disabled: !allDone,
        onClick: onOk
      }}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('go_to_organization')}
      classNames={{ header: styles.header }}
      {...props}
    >
      <Flex orientation="vertical" gap="middle">
        <Text color="colorTextSecondary">
          {allDone ? t('setup_completed_description') : t('we_are_setting')}
        </Text>

        <Divider className={styles.divider} />

        {showSpinner ? (
          <Flex justify="center" align="center">
            <Spin />
          </Flex>
        ) : (
          <>
            {totalCount > 0 && (
              <Progress
                percent={percent}
                status={progressStatus}
                strokeColor={
                  progressStatus === 'exception'
                    ? undefined
                    : token.colorPrimary
                }
                // The number stays visible next to the failure icon: how far the
                // build got before it broke is the first thing anybody asks.
                format={(pct) => {
                  if (progressStatus === 'success') {
                    return (
                      <CheckCircleFilled
                        style={{ color: token.colorPrimary }}
                      />
                    )
                  }
                  if (progressStatus === 'exception') {
                    return (
                      <Flex align="center" gap={token.marginXXS}>
                        <Text>{`${pct}%`}</Text>
                        <CloseCircleFilled />
                      </Flex>
                    )
                  }

                  return `${pct}%`
                }}
              />
            )}

            <div className={styles.stepsContainer}>
              <Steps orientation="vertical" items={items} />
            </div>

            {results.length > MAX_ITEMS && (
              <Button
                icon={
                  isCollapsed ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronUp size={16} />
                  )
                }
                onClick={onToggleCollapse}
              >
                {isCollapsed ? t('show_more') : t('show_less')}
              </Button>
            )}
          </>
        )}

        {hasError && (
          <Flex orientation="vertical" gap={8}>
            <Flex align="center" gap={6}>
              <Info size={20} color={token.colorError} />
              <Text color="colorError" strong>
                {`${t('something_went_wrong')}, ${t('please_try_again').toLowerCase()}.`}
              </Text>
            </Flex>

            <Button
              type="primary"
              icon={<RotateCcw size={16} />}
              onClick={onRetry}
              loading={isPending}
            >
              {t('retry')}
            </Button>
          </Flex>
        )}
      </Flex>
    </Modal>
  )
}
